import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Share2, Copy, Check, ArrowRight, TrendingUp, Award, X
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = [
  {
    id: 'linkedin',
    name: 'LinkedIn',
    color: '#0077B5',
    icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
  {
    id: 'facebook',
    name: 'Facebook',
    color: '#1877F2',
    icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    id: 'instagram',
    name: 'Instagram',
    color: '#E4405F',
    icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
      </svg>
    ),
  },
];

/* Score ring for the share card */
const ShareRing = ({ score }) => {
  const r = 42, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const c = score >= 70 ? '#22c55e' : score >= 50 ? '#D4A017' : '#ef4444';
  return (
    <svg width="100" height="100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={c} strokeWidth="6"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform="rotate(-90 50 50)" />
      <text x="50" y="54" textAnchor="middle" fill="white" fontSize="22" fontWeight="800" fontFamily="Sora, sans-serif">{score}</text>
    </svg>
  );
};

export default function VoortgangDelenPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api('get', '/api/platform/share-data')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const copyText = (platform) => {
    const text = data?.share_texts?.[platform] || '';
    navigator.clipboard.writeText(text).then(() => {
      setCopied(platform);
      toast.success('Tekst gekopieerd!');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const shareUrl = 'https://imperiaai.nl/scan';

  const openShare = (platform) => {
    const text = encodeURIComponent(data?.share_texts?.[platform] || '');
    const url = encodeURIComponent(shareUrl);
    let shareLink = '';
    if (platform === 'linkedin') shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    if (platform === 'facebook') shareLink = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    if (platform === 'instagram') {
      copyText('instagram');
      toast.info('Instagram tekst gekopieerd! Plak het in uw Instagram post.');
      return;
    }
    if (shareLink) window.open(shareLink, '_blank', 'width=600,height=500');
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!data?.success) return (
    <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
      <Share2 size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
      <p>Start eerst een scan om uw voortgang te delen.</p>
    </div>
  );

  const { company_name, score, score_label, done_count, total_count, share_texts } = data;

  return (
    <div data-testid="voortgang-delen-page">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
          Voortgang Delen
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Deel uw groei op social media en inspireer anderen om ook te groeien.
        </p>
      </div>

      {/* Share Banner Preview */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
        borderRadius: '18px', padding: '32px', marginBottom: '24px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative elements */}
        <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,160,23,0.15), transparent)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-20px', left: '30%', width: '150px', height: '150px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.08), transparent)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
          <ShareRing score={score} />
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
              Online Groeiscan
            </p>
            <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800, color: 'white', marginBottom: '8px' }}>
              {company_name}
            </h2>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', textTransform: 'uppercase' }}>Score</p>
                <p style={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>{score}/100 · {score_label}</p>
              </div>
              <div>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', textTransform: 'uppercase' }}>Opdrachten</p>
                <p style={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>{done_count}/{total_count} afgerond</p>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <img src="/logo-goud.png" alt="" style={{ height: '24px', opacity: 0.6, marginBottom: '4px' }}
              onError={e => { e.target.style.display = 'none'; }} />
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>imperiaai.nl</p>
          </div>
        </div>
      </div>

      {/* Share per platform */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {PLATFORMS.map(platform => {
          const Icon = platform.icon;
          const text = share_texts?.[platform.id] || '';

          return (
            <div key={platform.id} data-testid={`share-${platform.id}`} style={{
              background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px',
                borderBottom: '1px solid #f8fafc',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: `${platform.color}10`, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: platform.color,
                }}>
                  <Icon />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{platform.name}</p>
                  <p style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {platform.id === 'instagram' ? 'Kopieer tekst voor uw post' : 'Deel direct op uw profiel'}
                  </p>
                </div>
                <button
                  data-testid={`share-btn-${platform.id}`}
                  onClick={() => openShare(platform.id)}
                  style={{
                    background: platform.color, color: 'white', border: 'none',
                    borderRadius: '10px', padding: '8px 16px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <Share2 size={14} /> Delen
                </button>
              </div>

              {/* Text preview */}
              <div style={{ padding: '14px 20px', background: '#f8fafc' }}>
                <p style={{ fontSize: '12px', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {text}
                </p>
                <button
                  data-testid={`copy-btn-${platform.id}`}
                  onClick={() => copyText(platform.id)}
                  style={{
                    marginTop: '8px', background: 'white', border: '1px solid #e2e8f0',
                    borderRadius: '8px', padding: '6px 12px', cursor: 'pointer',
                    fontSize: '11px', fontWeight: 600, color: '#374151',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  {copied === platform.id ? <Check size={12} style={{ color: '#22c55e' }} /> : <Copy size={12} />}
                  {copied === platform.id ? 'Gekopieerd!' : 'Kopieer tekst'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tip */}
      <div style={{
        marginTop: '20px', background: 'rgba(212,160,23,0.06)', borderRadius: '12px',
        padding: '16px', border: '1px solid rgba(212,160,23,0.15)',
      }}>
        <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.6 }}>
          <strong style={{ color: '#D4A017' }}>Tip:</strong> Deel uw voortgang regelmatig op social media. Dit vergroot uw zichtbaarheid, toont dat u investeert in groei, en inspireert anderen om ook hun bedrijf te laten scannen. Win-win!
        </p>
      </div>
    </div>
  );
}
